import {type ChangeEvent, type JSX, type MouseEvent, useCallback, useEffect, useState} from 'react'

import {adminGetProducts, adminUpdateProduct} from '../../actions'
import {useAppDispatch, useAppSelector} from '../../hooks'
import type {AdminProduct} from '../../reducers/types'
import {classNames} from '../../utils'
import {CURRENCY_PRECISION} from "../../utils/app-utils";
import {Language, Localization} from '../../utils/Localization'
import {useErrorNotifications} from "../common/Notifications";


/**
 * Admin section's product list component.
 */
export default function AdminProducts(): JSX.Element {
  const products: AdminProduct[] = useAppSelector<AdminProduct[]>(state => state.admin.products)
  const pending = useAppSelector<boolean>(state => state.admin.productsPending)
  const dispatch = useAppDispatch()
  useErrorNotifications((state) => state.admin.productsError)

  useEffect(() => {
    dispatch(adminGetProducts())
  }, [dispatch])

  if (pending) {
    return (
      <div className='spinner-border' role='status'>
        <span className='visually-hidden'>Loading...</span>
      </div>
    )
  }

  const sortedProducts: AdminProduct[] = [...products].sort((p1, p2) => {
    const p1SortStr = `${p1.productType}-${p1.id}`
    const p2SortStr = `${p2.productType}-${p2.id}`

    if (p1SortStr > p2SortStr) {
      return 1
    }

    if (p1SortStr < p2SortStr) {
      return -1
    }

    return 0
  })

  return (
    <div className='form-outline pb-5'>
      <h1>Tuotteet</h1>
      <table className='table'>
        <thead>
        <tr>
          <th>Tunniste</th>
          <th>Tyyppi</th>
          <th>Kuvaus</th>
          <th>Hinta (&euro;)</th>
          <th>&nbsp;</th>
        </tr>
        </thead>
        <tbody>
        {sortedProducts.map(p => (
          <AdminProductsRow key={p.id} product={p}/>
        ))}
        </tbody>
      </table>
    </div>
  )
}

/* Child Components */

interface AdminProductsRowProps {
  product: AdminProduct
}

export function AdminProductsRow({product}: Readonly<AdminProductsRowProps>): JSX.Element {
  const [priceCentsInput, priceCents, resetPriceCents] = useOptionalPriceInput(product.priceCents)
  const dispatch = useAppDispatch()
  const saveDisabled = priceCents === product.priceCents

  function onSave(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault()

    if (saveDisabled) {
      return
    }

    dispatch(adminUpdateProduct(product.id, priceCents))
  }

  function onCancel(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault()
    resetPriceCents()
  }

  const saveClasses = {
    'btn btn-success btn-product-save me-3': true,
    disabled: saveDisabled
  }

  const cancelClasses = {
    'btn btn-danger btn-product-cancel': true,
    disabled: saveDisabled
  }

  return (
    <tr>
      <td>{product.id}</td>
      <td>{Localization.getLocalizedProductType(product.productType, Language.FI)}</td>
      <td>{product.description}</td>
      <td>{priceCentsInput}</td>
      <td>
        <a href='/#' className={classNames(saveClasses)} onClick={onSave} data-testid='admin-products-row-save'>Save</a>
        <a href='/#' className={classNames(cancelClasses)} onClick={onCancel}
           data-testid='admin-products-row-cancel'>Cancel</a>
      </td>
    </tr>
  )
}


/* Helper Functions */

function priceToInputValue(val: number | null): string {
  if (val === null) {
    return ''
  }

  return (val / 100).toFixed(CURRENCY_PRECISION)
}

function inputValueToPrice(val: string): number | null {
  if (val.trim() === '') {
    return null
  }

  return Math.round(parseFloat(val) * 100)
}

function useOptionalPriceInput(initialValue: number | null): [JSX.Element, number | null, () => void] {
  const [value, setValue] = useState(priceToInputValue(initialValue))
  const reset = useCallback(() => setValue(priceToInputValue(initialValue)), [initialValue, setValue])

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    setValue(e.target.value)
  }

  const numberValue = inputValueToPrice(value)
  const classes = {
    'price-input': true,
    dirty: numberValue !== initialValue
  }

  return [
    <input
      key='price-input'
      type='number'
      step='0.01'
      value={value}
      onChange={onChange}
      className={classNames(classes)}
      data-testid='optional-price-input'
    />,
    numberValue,
    reset
  ]
}
